// shadi-arts-portfolio Firebase\client\src\components\Admin\ArtworkManager.jsx

import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

// Cloudinary upload function (reuses your existing backend)
const uploadToCloudinary = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const API_URL =
          process.env.NODE_ENV === "production"
            ? "https://us-central1-shadi-arts.cloudfunctions.net/api"
            : "http://127.0.0.1:5001/shadi-arts/us-central1/api";

        const response = await fetch(`${API_URL}/upload-photos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            photos: [
              {
                data: reader.result,
                name: file.name,
                size: file.size,
                type: file.type,
              },
            ],
          }),
        });

        const data = await response.json();
        if (data.success && data.photos && data.photos.length > 0) {
          resolve(data.photos[0].url);
        } else {
          reject(new Error("Upload failed"));
        }
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const ArtworkManager = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    arabicTitle: "",
    category: "",
    description: "",
    arabicDescription: "",
    year: new Date().getFullYear(),
    size: "A4",
    tags: [],
    imageFile: null,
    // 🆕 الحقول الجديدة للفيديو
    longVideoId: "",
    shortVideoId: "",
  });

  const [preview, setPreview] = useState(null);

  // Fetch artworks from Firestore
  useEffect(() => {
    fetchArtworks();
  }, []);

  const fetchArtworks = async () => {
    try {
      const snapshot = await getDocs(collection(db, "artworks"));
      const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setArtworks(items.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) {
      console.error("Error fetching artworks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, imageFile: file });
    setPreview(URL.createObjectURL(file));
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(",").map((tag) => tag.trim());
    setFormData({ ...formData, tags });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.imageFile && !editingArtwork) {
      alert("Please select an image");
      return;
    }

    setSubmitting(true);

    try {
      let imageUrl = editingArtwork?.imageUrl;

      if (formData.imageFile) {
        imageUrl = await uploadToCloudinary(formData.imageFile);
      }

      const artworkData = {
        title: formData.title,
        arabicTitle: formData.arabicTitle,
        category: formData.category,
        description: formData.description,
        arabicDescription: formData.arabicDescription,
        year: formData.year,
        size: formData.size,
        tags: formData.tags,
        imageUrl,
        // 🆕 حفظ حقول الفيديو (لو مكتوبة)
        longVideoId: formData.longVideoId || "",
        shortVideoId: formData.shortVideoId || "",
        updatedAt: new Date().toISOString(),
      };

      if (editingArtwork) {
        await updateDoc(doc(db, "artworks", editingArtwork.id), artworkData);
        alert("Artwork updated successfully!");
      } else {
        artworkData.order = artworks.length + 1;
        artworkData.createdAt = new Date().toISOString();
        await addDoc(collection(db, "artworks"), artworkData);
        alert("Artwork added successfully!");
      }

      resetForm();
      fetchArtworks();
    } catch (error) {
      console.error("Error saving artwork:", error);
      alert("Error saving artwork: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (artwork) => {
    if (window.confirm(`Delete "${artwork.title}"? This cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, "artworks", artwork.id));
        alert("Artwork deleted successfully!");
        fetchArtworks();
      } catch (error) {
        console.error("Error deleting artwork:", error);
        alert("Error deleting artwork");
      }
    }
  };

  const resetForm = () => {
    setEditingArtwork(null);
    setFormData({
      title: "",
      category: "",
      description: "",
      arabicDescription: "",
      year: new Date().getFullYear(),
      size: "A4",
      tags: [],
      imageFile: null,
      arabicTitle: "",
      // 🆕 إعادة تعيين حقول الفيديو
      longVideoId: "",
      shortVideoId: "",
    });
    setPreview(null);
  };

  const startEdit = (artwork) => {
    setEditingArtwork(artwork);
    setFormData({
      title: artwork.title,
      arabicTitle: artwork.arabicTitle || "",
      category: artwork.category,
      description: artwork.description,
      arabicDescription: artwork.arabicDescription || "",
      year: artwork.year,
      size: artwork.size,
      tags: artwork.tags,
      imageFile: null,
      // 🆕 تحميل حقول الفيديو لو موجودة
      longVideoId: artwork.longVideoId || "",
      shortVideoId: artwork.shortVideoId || "",
    });
    setPreview(artwork.imageUrl);
  };

  if (loading) return <div className="loading">Loading artworks...</div>;

  return (
    <div className="artwork-manager">
      {/* Form Section */}
      <div className="form-section">
        <h3>{editingArtwork ? "✏️ Edit Artwork" : "➕ Add New Artwork"}</h3>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-row">
            <div className="form-group">
              <label>Title (English) *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>العنوان (Arabic) *</label>
              <input
                type="text"
                value={formData.arabicTitle}
                onChange={(e) =>
                  setFormData({ ...formData, arabicTitle: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Category *</label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                required
              >
                <option value="">Select Category</option>
                {/* أقسام العملاء */}
                <option value="couples">Couples (أزواج)</option>
                <option value="families">Families (عائلات)</option>
                <option value="children">Children (أطفال)</option>
                <option value="elders">Elders (كبار السن)</option>
                <option value="unknown">Unknown (مجهول)</option>

                {/* أقسام المشاهير */}
                <option value="sport-legends">
                  Sport Legends (أساطير الرياضة)
                </option>
                <option value="actors">Actors (نجوم التمثيل)</option>
                <option value="singers">Singers (نجوم الغناء)</option>
                <option value="icons">Icons (شخصيات ملهمة)</option>
                <option value="media-personalities">
                  Media Personalities (شخصيات إعلامية)
                </option>

                {/* أقسام فنية */}
                <option value="movie-posters">
                  Movie Posters (بوسترات أفلام)
                </option>

                <option value="pets">Pets</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description (English) * </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>الوصف (Arabic) * </label>
            <textarea
              value={formData.arabicDescription}
              onChange={(e) =>
                setFormData({ ...formData, arabicDescription: e.target.value })
              }
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Year</label>
              <input
                type="text"
                value={formData.year}
                onChange={(e) =>
                  setFormData({ ...formData, year: e.target.value })
                }
              />
            </div>

            <div className="form-group">
              <label>Size</label>
              <select
                value={formData.size}
                onChange={(e) =>
                  setFormData({ ...formData, size: e.target.value })
                }
              >
                <option value="A4">A4</option>
                <option value="A3">A3</option>
                <option value="A2">A2</option>
                <option value="Custom">Custom</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Tags (comma-separated)</label>
            <input
              type="text"
              value={formData.tags.join(", ")}
              onChange={handleTagsChange}
              placeholder="e.g., portrait, realistic, charcoal"
            />
          </div>

          {/* 🆕 قسم الفيديوهات على يوتيوب */}
          <div className="form-row video-inputs-row">
            <div className="form-group">
              <label>🎬 Long Video ID (YouTube)</label>
              <input
                type="text"
                value={formData.longVideoId}
                onChange={(e) =>
                  setFormData({ ...formData, longVideoId: e.target.value })
                }
                placeholder="e.g., HccYSaFZj0w"
              />
              <small className="form-hint">
                فقط الـ ID (بعد v=)
              </small>
            </div>

            <div className="form-group">
              <label>⚡ Short Video ID (YouTube Shorts)</label>
              <input
                type="text"
                value={formData.shortVideoId}
                onChange={(e) =>
                  setFormData({ ...formData, shortVideoId: e.target.value })
                }
                placeholder="e.g., AbC123XyZ"
              />
              <small className="form-hint">
                فقط الـ ID (بعد v=)
              </small>
            </div>
          </div>

          <div className="form-group">
            <label>Artwork Image {!editingArtwork && "*"}</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              required={!editingArtwork}
            />
            {preview && (
              <img src={preview} alt="Preview" className="image-preview" />
            )}
          </div>

          <div className="form-buttons">
            <button type="submit" disabled={submitting} className="btn-gold">
              {submitting
                ? "Saving..."
                : editingArtwork
                  ? "Update Artwork"
                  : "Add Artwork"}
            </button>
            {editingArtwork && (
              <button type="button" onClick={resetForm} className="btn-black">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* List Section */}
      <div className="list-section">
        <h3>📋 Existing Artworks ({artworks.length})</h3>
        <div className="artworks-table-container">
          <table className="artworks-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Category</th>
                <th>Size</th>
                <th>Video</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {artworks.map((artwork) => (
                <tr key={artwork.id}>
                  <td>
                    <img
                      src={artwork.imageUrl}
                      alt={artwork.title}
                      className="table-image"
                    />
                  </td>
                  <td>{artwork.title}</td>
                  <td>{artwork.category}</td>
                  <td>{artwork.size}</td>
                  <td>
                    {/* 🆕 عرض حالة الفيديو */}
                    {artwork.longVideoId && "🎬 "}
                    {artwork.shortVideoId && "⚡"}
                    {!artwork.longVideoId && !artwork.shortVideoId && "—"}
                  </td>
                  <td>
                    <button
                      onClick={() => startEdit(artwork)}
                      className="btn-edit"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(artwork)}
                      className="btn-delete"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ArtworkManager;